import { Check, EmailOutlined, Link } from "@mui/icons-material";
import copy from "copy-to-clipboard";
import { type TFunction } from "next-i18next";
import React, { useEffect, useState } from "react";
import { type Input as MarketDataInput, mapMarketDataInput } from "~/components/market-data/MarketDataBrowser";
import { Button } from "~/components/ui/core/Button";
import { LegacyHeading } from "~/components/ui/core/LegacyHeading";
import { LegacyModal } from "~/components/ui/core/LegacyModal";
import { LegacyText } from "~/components/ui/core/LegacyText";
import { TextInput } from "~/components/ui/core/TextInput";
import { TextareaInput } from "~/components/ui/core/TextareaInput";
import { config } from "~/config";
import { useApi } from "~/hooks/useApi";
import { useForm } from "~/hooks/useForm";
import { useListJobsQuery } from "~/hooks/useQueries";
import { useSession } from "~/hooks/useSession";
import { trackMarketDataSearchShared } from "~/lib/external/segment/web/events";
import { useI18n } from "~/lib/i18n/useI18n";
import { compact, uniq } from "~/lib/lodash";
import { humanJoin } from "~/lib/utils";
import { type ShareMarketDataSearchResponse } from "~/pages/api/market-data-search/share-market-data-search";
import { MarketDataButtonPlaceholder, SendMarketDataSearchSchema } from "~/pages/api/send-market-data-search";
import { formatLevel } from "~/services/employee/employeeLevel";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  input: MarketDataInput;
};

type FormValues = {
  email: string;
  body: string;
  hash: string | null;
};

const generateDefaultEmailBody = (t: TFunction, input: MarketDataInput, jobTitles: string[]): string => {
  const levels = input.levels.map((level) => formatLevel(t, level, !input.mergeAdvancedLevels)).join(" / ");
  const countries = humanJoin(uniq(input.market.locations.map((location) => location.country.name)));

  const jobTitlesLabel = t("pages.market-data.market-data-share-modal.job-titles-label", {
    count: input.market.jobIds.length,
  });
  const levelsLabel = t("pages.market-data.market-data-share-modal.levels-label", { count: input.levels.length });

  return t("pages.market-data.market-data-share-modal.here-a-look-at-figures-market-data", {
    jobTitles,
    jobTitlesLabel,
    levels,
    levelsLabel,
    countries,
    MarketDataButtonPlaceholder,
  });
};

export const MarketDataShareModal: React.FC<Props> = ({ isOpen, onClose, input }) => {
  const { t } = useI18n();
  const { apiFetch } = useApi();
  const { user } = useSession();
  const [isSent, setIsSent] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const { data: jobsData } = useListJobsQuery({}, { enabled: isOpen });

  useEffect(() => {
    if (!jobsData || !isOpen) return;

    const jobTitles = compact(
      input.market.jobIds.map((jobId) => {
        const job = jobsData.jobs.find((job) => `${job.id}` === jobId);
        return job?.name;
      })
    );

    form.updateValues({
      body: generateDefaultEmailBody(t, input, jobTitles),
    });
  }, [jobsData, isOpen]);

  const form = useForm<FormValues>({
    initialValues: {
      email: "",
      body: "",
      hash: null,
    },
    validationSchema: SendMarketDataSearchSchema,
    onSubmit: async (values) => {
      if (isSent) {
        return;
      }

      await apiFetch<ShareMarketDataSearchResponse>("/api/send-market-data-search", {
        body: values,
        successMessage: `Email successfully sent to ${values.email}`,
      });

      await trackMarketDataSearchShared({ mode: "EMAIL" });

      setIsSent(true);
    },
  });

  const url = form.values.hash ? `${config.app.url}/share/${form.values.hash}` : null;

  const shareMarketDataSearch = async () => {
    const res = await apiFetch<ShareMarketDataSearchResponse>("/api/market-data-search/share-market-data-search", {
      body: {
        search: {
          ...mapMarketDataInput(input),
          useTestData: false,
          mergeAdvancedLevels: !user?.company.useAdvancedLevels,
        },
      },
    });

    void form.setFieldValue("hash", res.hash);
  };

  const onCopyClick = async () => {
    if (form.values.hash && url) {
      copy(url);

      if (!isCopied) {
        await trackMarketDataSearchShared({ mode: "COPY_TO_CLIPBOARD" });
      }

      setIsCopied(true);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsCopied(false);
      setIsSent(false);

      void form.setFieldValue("hash", null);

      void shareMarketDataSearch();
    }
  }, [isOpen, user]);

  useEffect(() => {
    setIsSent(false);
  }, [form.values.email]);

  return (
    <LegacyModal className="w-[800px]" isOpen={isOpen} title="Share 🤗" onClose={onClose}>
      <div className="ml-1 mt-4">
        <LegacyText icon="information" size="md">
          {t("pages.market-data.market-data-share-modal.only-access-aggregated-anonymised-market-information")}
        </LegacyText>
      </div>

      <form className="mt-12" onSubmit={form.handleSubmit}>
        <LegacyHeading size="lg" className="ml-1 normal-case">
          {t("pages.market-data.market-data-share-modal.send-by-email")}
        </LegacyHeading>

        <div className="mt-4 flex">
          <TextInput
            value={form.values.email}
            onChange={(event) => {
              void form.setFieldValue("email", event.target.value);
            }}
            type="email"
            placeholder="contact@email.com"
          />
          <Button
            type="submit"
            variant="contained"
            className="ml-2 shrink-0"
            startIcon={isSent ? <Check /> : <EmailOutlined />}
            disabled={form.isSubmitting || !form.isValid}
          >
            {isSent
              ? t("pages.market-data.market-data-share-modal.email-sent")
              : t("pages.market-data.market-data-share-modal.send-email")}
          </Button>
        </div>

        <TextareaInput
          className="mt-2"
          rows={7}
          value={form.values.body}
          onChange={(event) => {
            void form.setFieldValue("body", event.target.value);
          }}
        />
      </form>

      <div className="-mx-3 my-10 border-t" />

      <div>
        <LegacyHeading size="lg" className="ml-1 normal-case">
          {t("pages.market-data.market-data-share-modal.direct-link")}
        </LegacyHeading>

        <div className="mt-4 flex">
          <TextInput readOnly value={url ?? "Generating unique link..."} />
          <Button
            className="ml-2 shrink-0"
            startIcon={isCopied ? <Check /> : <Link />}
            disabled={!url}
            variant="outlined"
            onClick={onCopyClick}
          >
            {isCopied
              ? t("pages.market-data.market-data-share-modal.copied")
              : t("pages.market-data.market-data-share-modal.copy-link")}
          </Button>
        </div>
      </div>
    </LegacyModal>
  );
};
